import React from 'react';
import PropTypes from 'prop-types';
import AppGridContainer from '@tenant/core/AppGridContainer';
import { Box, FormHelperText, Grid, TextField } from '@mui/material';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import AppCKEditor from '@tenant/core/AppCKEditor';
import replaceBlobVideoToCdn from 'helpers/replaceBlobVideoToCdn';

const DescriptionStep = ({
  errors = {},
  values = {},
  touched = {},
  setFieldValue = () => {},
  keyResetForm = 0,
}) => {
  return (
    <AppGridContainer
      sx={{
        '& .required-sign': {
          color: 'error.main',
        },
      }}
    >
      <Grid item xs={12} sm={12}>
        <Grid container spacing={5}>
          <Grid item xs={12} sm={6}>
            <Box
              component='p'
              display={'flex'}
              alignItems={'center'}
              color='text.primary'
              fontSize={14}
              mb={2}
            >
              Meta Title{' '}
              <AppGlossary title='The meta title specifically refers to the title of the web page, which is typically displayed in search engine results, browser tabs, and social media shares' />
            </Box>
            <TextField
              fullWidth
              placeholder='E.g. Your app | Joblogic® Marketplace'
              error={!!errors.MetaTitle}
              helperText={errors.MetaTitle}
              value={values.MetaTitle ?? ''}
              onChange={(e) => {
                setFieldValue('MetaTitle', e.target.value);
              }}
            />
            <Box sx={{ '& .MuiFormHelperText-root': { fontSize: 12, ml: 3 } }}>
              <FormHelperText>E.g. App | Joblogic® Marketplace</FormHelperText>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              component='p'
              display={'flex'}
              alignItems={'center'}
              color='text.primary'
              fontSize={14}
              mb={2}
            >
              Meta Description
              <AppGlossary title="It provides a brief summary or snippet of a web page's content, typically displayed beneath the page title in search engine results" />
            </Box>
            <TextField
              fullWidth
              placeholder='Describe your app'
              error={!!errors.MetaDescription}
              helperText={errors.MetaDescription}
              value={values.MetaDescription ?? ''}
              onChange={(e) => {
                setFieldValue('MetaDescription', e.target.value);
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <Box component='p' color='text.primary' fontSize={14} mb={2}>
          Short Description
          <AppGlossary
            title='A brief summary of your app in one or two sentences.
          This concise overview helps users quickly understand what your app does. It’s typically displayed in search results or overview sections'
          />
        </Box>
        <TextField
          placeholder='Enter text'
          name='ShortDescription'
          error={touched.ShortDescription && !!errors.ShortDescription}
          helperText={touched.ShortDescription ? errors.ShortDescription : ''}
          value={values.ShortDescription ?? ''}
          onChange={async (e) => {
            setFieldValue('ShortDescription', e.target.value);
          }}
          onBlur={() => {
            setFieldValue('ShortDescription', values.ShortDescription?.trim());
          }}
          fullWidth
          minRows={4}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        <Box component='p' color='text.primary' fontSize={14} mb={2}>
          Long Description
          <AppGlossary
            title='A detailed explanation of your app’s features, functionality, and benefits.
          This section allows you to provide in-depth information, helping users fully understand the app’s capabilities, target audience, and unique advantages. Use this space to highlight key features, usage scenarios, and any requirements.'
          />
        </Box>
        <Box sx={{ '& .ck-editor__editable': { minHeight: 350 } }}>
          <AppCKEditor
            key={keyResetForm}
            initialData={values?.LongDescription}
            data={values?.LongDescription}
            error={touched.LongDescription && !!errors.LongDescription}
            helperText={touched.LongDescription ? errors.LongDescription : ''}
            imageUploader={{
              url: `${process.env.REACT_APP_HOST}api/apps/upload-image`,
              token: localStorage.getItem('token'),
              payload: {
                AppId: values.AppId,
                AppCode: values.AppCodeImage ?? '',
              },
            }}
            handleChange={(event, editor) => {
              const data = editor.getData();
              setFieldValue('LongDescription', data);
            }}
            handleBlur={(event, editor) => {
              const data = editor.getData();
              const newData = replaceBlobVideoToCdn(data);
              setFieldValue('LongDescription', newData);
            }}
          />
        </Box>
      </Grid>
    </AppGridContainer>
  );
};

export default React.memo(DescriptionStep);

DescriptionStep.propTypes = {
  errors: PropTypes.object,
  values: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func,
  keyResetForm: PropTypes.number,
};
